import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import React, { useEffect, useState } from 'react'

import {
  claimPlannerTimetable,
  createPlannerLayout,
  deletePlannerPlanCell,
  fetchPlannerPlanEditor,
  fetchPlannerPlanRepository,
  setPlannerPlanStatus,
  upsertPlannerPlanCell
} from 'api'
import { showToast } from 'app/helpers'

const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri']
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri']

const toLines = (arr) => (Array.isArray(arr) ? arr.join('\n') : '')
// LOSSLESS split for the typing round-trip — must NOT trim/filter, or a space
// typed at the end of a word (i.e. mid-sentence, before the next word) would be
// stripped on every keystroke and the space would never stick.
const splitLinesRaw = (s) => (s || '').split('\n')
// CLEANUP (blur/save only): trim each line and drop empties.
const fromLines = (s) =>
  (s || '')
    .split('\n')
    .map((x) => x.trim())
    .filter(Boolean)
const stepText = (s) => (typeof s === 'string' ? s : (s && s.text) || '')
const stepsToText = (steps) =>
  (Array.isArray(steps) ? steps : []).map(stepText).join('\n')
const textToStepsRaw = (s) =>
  splitLinesRaw(s).map((text, i) => ({ label: `Step ${i + 1}`, text }))
const textToSteps = (s) =>
  fromLines(s).map((text, i) => ({ label: `Step ${i + 1}`, text }))

const blankDraft = (isMulti) => ({
  is_multi: !!isMulti,
  is_custom: false,
  subject: '',
  activity: '',
  title: '',
  outcomes: [],
  steps: [],
  visual_evolution: '',
  layout: '',
  materials: [],
  vocabulary: [],
  differentiation: {},
  room: '',
  reservations: []
})

const draftFromCell = (c) => ({
  is_multi: !!c.is_multi,
  is_custom: !!c.is_custom,
  subject: c.subject || '',
  activity: c.activity || '',
  title: c.title || '',
  outcomes: c.outcomes || [],
  steps: c.steps || [],
  visual_evolution: c.visual_evolution || '',
  layout: c.layout || '',
  materials: c.materials || [],
  vocabulary: c.vocabulary || [],
  differentiation: c.differentiation || {},
  room: c.room || '',
  reservations: c.reservations || []
})

const ROSection = ({ title, children }) => (
  <Box mt={1}>
    <Typography
      variant="caption"
      style={{ color: '#9a8f82', fontWeight: 600, display: 'block' }}
    >
      {title}
    </Typography>
    {children}
  </Box>
)

// A read-only view of a saved lesson — shown when the plan is SUBMITTED (locked),
// so a teacher/admin can still tap a filled slot and see what was planned. Editing
// requires reopening the plan.
const ReadOnlyLesson = ({ d, layouts, rooms }) => {
  const layoutName = (layouts.find((l) => l.uuid === d.layout) || {}).label
  const roomName = (rooms.find((r) => r.uuid === d.room) || {}).name
  const diff = d.differentiation || {}
  return (
    <Box display="flex" flexDirection="column" style={{ gap: 4 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
          {d.title || '(activity)'}
        </Typography>
        <Typography
          variant="caption"
          style={{ color: d.is_custom ? '#b07b1e' : '#2d6a4f' }}
        >
          {d.is_custom ? '✎ your own' : '📚 repo'}
        </Typography>
      </Box>
      <Paper
        variant="outlined"
        style={{ padding: 8, background: '#f5f2ec', borderColor: '#e3dccf' }}
      >
        <Typography variant="caption" style={{ color: '#7a6f65' }}>
          🔒 This plan is submitted — view only. Reopen the plan to edit.
        </Typography>
      </Paper>
      {(layoutName || roomName) && (
        <Typography variant="body2" style={{ marginTop: 6 }}>
          {layoutName ? `📐 ${layoutName}` : ''}
          {layoutName && roomName ? '    ' : ''}
          {roomName ? `🚪 ${roomName}` : ''}
        </Typography>
      )}
      {!!(d.outcomes || []).length && (
        <ROSection title="OUTCOMES">
          {d.outcomes.map((o, i) => (
            <Typography key={i} variant="body2">
              • {o}
            </Typography>
          ))}
        </ROSection>
      )}
      {!!(d.materials || []).length && (
        <ROSection title="MATERIALS">
          {d.materials.map((m, i) => (
            <Typography key={i} variant="body2">
              • {m}
            </Typography>
          ))}
        </ROSection>
      )}
      {!!(d.steps || []).length && (
        <ROSection title="STEPS">
          {d.steps.map((s, i) => (
            <Typography key={i} variant="body2">
              <strong>{(s && s.label) || `Step ${i + 1}`}</strong> —{' '}
              {stepText(s)}
            </Typography>
          ))}
        </ROSection>
      )}
      {!!(d.vocabulary || []).length && (
        <ROSection title="VOCABULARY">
          <Typography variant="body2">{d.vocabulary.join(', ')}</Typography>
        </ROSection>
      )}
      {(diff.simpler || diff.extension || diff.specific) && (
        <ROSection title="DIFFERENTIATION">
          {diff.simpler ? (
            <Typography variant="body2">🌱 {diff.simpler}</Typography>
          ) : null}
          {diff.extension ? (
            <Typography variant="body2">🚀 {diff.extension}</Typography>
          ) : null}
          {diff.specific ? (
            <Typography variant="body2">🤝 {diff.specific}</Typography>
          ) : null}
        </ROSection>
      )}
    </Box>
  )
}

// A repository activity card (expandable, with "Use"), mirroring the app.
const RepoCard = ({ activity, picked, onUse }) => {
  const [open, setOpen] = useState(false)
  const a = activity
  return (
    <Paper
      variant="outlined"
      style={{
        padding: 10,
        marginBottom: 8,
        borderRadius: 10,
        borderColor: picked ? '#2d6a4f' : undefined,
        borderWidth: picked ? 2 : 1
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
      >
        <Box pr={1}>
          <Typography variant="body2" style={{ fontWeight: 700 }}>
            {a.title}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {a.subject_label || ''}
          </Typography>
        </Box>
        <Box style={{ whiteSpace: 'nowrap' }}>
          <Button size="small" onClick={() => setOpen((o) => !o)}>
            {open ? 'Hide ▲' : 'Show ▼'}
          </Button>
          <Button
            size="small"
            variant="contained"
            color="primary"
            onClick={onUse}
          >
            Use
          </Button>
        </Box>
      </Box>
      {open && (
        <Box mt={1}>
          {!!(a.outcomes || []).length && (
            <>
              <Typography
                variant="caption"
                style={{ color: '#9a8f82', fontWeight: 600 }}
              >
                OUTCOMES
              </Typography>
              {a.outcomes.map((o, i) => (
                <Typography key={i} variant="body2">
                  • {o}
                </Typography>
              ))}
            </>
          )}
          {!!(a.materials || []).length && (
            <>
              <Typography
                variant="caption"
                style={{ color: '#9a8f82', fontWeight: 600 }}
              >
                MATERIALS
              </Typography>
              {a.materials.map((m, i) => (
                <Typography key={i} variant="body2">
                  • {m}
                </Typography>
              ))}
            </>
          )}
          {!!(a.steps || []).length && (
            <>
              <Typography
                variant="caption"
                style={{ color: '#9a8f82', fontWeight: 600 }}
              >
                STEPS
              </Typography>
              {a.steps.map((s, i) => (
                <Typography key={i} variant="body2">
                  <strong>{(s && s.label) || `Step ${i + 1}`}</strong> —{' '}
                  {stepText(s)}
                </Typography>
              ))}
            </>
          )}
        </Box>
      )}
    </Paper>
  )
}

/**
 * The teacher's plan-filling, in Alfred. Weekly grid (timetable rows × Mon–Fri);
 * fixed routine slots locked; plannable slots open a two-step editor (pick an
 * activity from the repository or your own → customize) mirroring the app.
 * Same rules as the app (custom cap + submitted lock, enforced server-side).
 */
const PlanEditor = ({ centre, grade, classKey, weekStart, onClose }) => {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [slot, setSlot] = useState(null) // { rowIndex, weekday, kind, label }
  const [draft, setDraft] = useState(null)
  const [step, setStep] = useState('pick') // 'pick' | 'customize'
  const [repo, setRepo] = useState([])
  const [loadingRepo, setLoadingRepo] = useState(false)
  const [saving, setSaving] = useState(false)
  const [warnings, setWarnings] = useState(null)
  const [newLayout, setNewLayout] = useState('') // "add your own layout" input
  const [vocabInput, setVocabInput] = useState('') // vocabulary tag input
  const [extraLayouts, setExtraLayouts] = useState([]) // teacher-created layouts
  const [forcePicker, setForcePicker] = useState(false) // "change timetable"
  const [previewId, setPreviewId] = useState('') // timetable being previewed

  const addLayout = async () => {
    const label = newLayout.trim()
    if (!label) return
    // Layouts are a global list teachers may append to (Layout model D-note).
    const slug = label
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '')
    const key = `${(slug || 'layout').slice(0, 33)}-${Math.random()
      .toString(36)
      .slice(2, 6)}`
    try {
      const created = await createPlannerLayout({ label, key })
      if (created && created.uuid) {
        setExtraLayouts((xs) => [...xs, created])
        setDraft((x) => ({ ...x, layout: created.uuid }))
      }
      setNewLayout('')
    } catch (e) {
      showToast('Failed to add layout', 'danger')
    }
  }

  const load = async () => {
    setLoading(true)
    try {
      const dd = await fetchPlannerPlanEditor({
        queries: { centre, grade, class_key: classKey, week_start: weekStart }
      })
      setData(dd)
    } catch (e) {
      showToast('Failed to load the plan', 'danger')
    }
    setLoading(false)
  }

  // Claim (or switch to) a grade timetable for this class, then reload the grid.
  const claimTimetable = async (templateUuid) => {
    setSaving(true)
    try {
      await claimPlannerTimetable({
        centre,
        grade,
        class_key: classKey,
        template: templateUuid
      })
      setForcePicker(false)
      await load()
    } catch (e) {
      showToast((e && e.message) || 'Could not select that timetable', 'danger')
    }
    setSaving(false)
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [centre, grade, classKey, weekStart])

  const loadRepo = async (gradeGroup, subjectKey) => {
    setLoadingRepo(true)
    try {
      // Filter the picker to the slot's subject (a Language slot lists only
      // Language activities). subjectKey is the ActivitySubject slug on the cell;
      // when absent (e.g. a subject-agnostic slot) we show all of the grade's.
      const queries = { grade_group: gradeGroup }
      if (subjectKey) queries.subject_key = subjectKey
      // ...and to the plan's curriculum slot. Number the week by the month it
      // mostly falls in — its Thursday — not its Monday: the week of Mon 31 Aug
      // is "September week 1" (Thu 3 Sep), matching how HQ tags activities.
      // Using the Monday gave "August week 5", which matched nothing at month
      // boundaries and showed an empty repository.
      const d = new Date(weekStart)
      if (!Number.isNaN(d.getTime())) {
        const thu = new Date(d)
        thu.setUTCDate(thu.getUTCDate() + 3)
        queries.month = thu.getUTCMonth() + 1
        queries.week = Math.ceil(thu.getUTCDate() / 7)
      }
      const r = await fetchPlannerPlanRepository({ queries })
      setRepo((r && r.activities) || [])
    } catch (e) {
      setRepo([])
    }
    setLoadingRepo(false)
  }

  const submitted = data && data.status === 'submitted'
  const cells = (data && data.cells) || []
  const cellFor = (rowIndex, weekday) =>
    cells.find((c) => c.row_index === rowIndex && c.weekday === weekday)

  const openSlot = (row, weekday) => {
    const meta = (row.cells || {})[DAY_KEYS[weekday]] || {}
    if (meta.kind === 'FIXED') return
    const existing = cellFor(row.row_index, weekday)
    // A submitted plan is locked for editing, but a FILLED slot still opens
    // READ-ONLY so the teacher/admin can review what was planned. An empty slot
    // on a locked plan has nothing to show, so it stays closed.
    if (submitted && !existing) return
    setSlot({
      rowIndex: row.row_index,
      weekday,
      kind: meta.kind,
      label: meta.label || row.time_label
    })
    setDraft(
      existing ? draftFromCell(existing) : blankDraft(meta.kind === 'PICK')
    )
    setStep(existing ? 'customize' : 'pick')
    setWarnings(null)
    loadRepo(data.grade_group, meta.subject_key) // repo filtered to this slot's subject
  }

  const useActivity = (a) => {
    setDraft((x) => ({
      ...x,
      is_custom: false,
      activity: a.uuid,
      subject: a.subject || '',
      title: a.title || '',
      outcomes: a.outcomes || [],
      steps: a.steps || [],
      materials: a.materials || [],
      visual_evolution: a.visual_evolution || ''
    }))
    setStep('customize')
  }

  const save = async () => {
    setSaving(true)
    try {
      const resp = await upsertPlannerPlanCell({
        centre,
        grade,
        class_key: classKey,
        week_start: weekStart,
        row_index: slot.rowIndex,
        weekday: slot.weekday,
        ...draft
      })
      const w = resp && resp.warnings
      const hasW = w && (w.room || []).length + (w.inventory || []).length > 0
      if (hasW) {
        setWarnings(w)
        showToast('Saved — with scheduling warnings', 'warning')
      } else {
        showToast('Cell saved', 'success')
        setSlot(null)
      }
      await load()
    } catch (e) {
      const msg =
        (e && e.response && e.response.data && e.response.data.detail) ||
        'Failed to save'
      showToast(msg, 'danger')
    }
    setSaving(false)
  }

  const clearCell = async () => {
    setSaving(true)
    try {
      await deletePlannerPlanCell({
        centre,
        grade,
        class_key: classKey,
        week_start: weekStart,
        row_index: slot.rowIndex,
        weekday: slot.weekday
      })
      showToast('Cell cleared', 'success')
      setSlot(null)
      await load()
    } catch (e) {
      showToast('Failed to clear', 'danger')
    }
    setSaving(false)
  }

  const changeStatus = async (action) => {
    try {
      await setPlannerPlanStatus({
        centre,
        grade,
        class_key: classKey,
        week_start: weekStart,
        action
      })
      showToast(
        action === 'submit' ? 'Plan submitted' : 'Plan reopened',
        'success'
      )
      await load()
    } catch (e) {
      showToast('Failed to update status', 'danger')
    }
  }

  if (loading) return <CircularProgress />
  if (!data) return null

  // Before a class has a timetable (or when switching it), show the picker.
  if (data.needs_claim || forcePicker) {
    const options = data.available_timetables || []
    return (
      <Box mt={2}>
        <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
          Pick a timetable for this class
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Choose one of the grade&apos;s timetables. Each timetable can be used
          by only one class at this centre, so one already taken by another
          class can&apos;t be picked.
        </Typography>
        {!options.length ? (
          <Typography
            variant="body2"
            color="textSecondary"
            style={{ marginTop: 12 }}
          >
            No timetables have been set up for this grade yet. Ask an admin to
            add one under HQ → Programs → Preschool → Timetables.
          </Typography>
        ) : (
          <Box display="flex" flexWrap="wrap" style={{ gap: 12, marginTop: 8 }}>
            {options.map((t) => (
              <Paper
                key={t.template}
                variant="outlined"
                style={{
                  width: 220,
                  padding: 14,
                  borderRadius: 12,
                  opacity: t.is_taken ? 0.55 : 1,
                  borderColor: t.is_mine ? '#2d6a4f' : undefined,
                  borderWidth: t.is_mine ? 2 : 1
                }}
              >
                <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                  {t.name}
                </Typography>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  style={{ display: 'block', marginBottom: 10 }}
                >
                  {t.is_mine
                    ? 'Current timetable'
                    : t.is_taken
                    ? 'Taken by another class'
                    : 'Available'}
                </Typography>
                <Box display="flex" style={{ gap: 8 }}>
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    disabled={t.is_taken || t.is_mine || saving}
                    onClick={() => claimTimetable(t.template)}
                  >
                    {saving ? 'Selecting…' : 'Use this'}
                  </Button>
                  <Button
                    size="small"
                    onClick={() =>
                      setPreviewId((id) =>
                        id === t.template ? '' : t.template
                      )
                    }
                  >
                    {previewId === t.template ? 'Hide' : 'Preview'}
                  </Button>
                </Box>
              </Paper>
            ))}
          </Box>
        )}

        {/* Schedule preview — so the teacher picks the grid matching their day */}
        {(() => {
          const t = options.find((o) => o.template === previewId)
          if (!t) return null
          const prows = t.rows || []
          return (
            <Box mt={2} style={{ overflowX: 'auto' }}>
              <Typography variant="subtitle2" gutterBottom>
                {t.name} — schedule
              </Typography>
              {!prows.length ? (
                <Typography variant="body2" color="textSecondary">
                  This timetable has no rows yet.
                </Typography>
              ) : (
                <table
                  style={{
                    borderCollapse: 'collapse',
                    minWidth: 640,
                    fontSize: 13
                  }}
                >
                  <thead>
                    <tr>
                      <th
                        style={{
                          textAlign: 'left',
                          padding: 6,
                          borderBottom: '1px solid #e0d9cf'
                        }}
                      >
                        Time
                      </th>
                      {DAY_LABELS.map((d) => (
                        <th
                          key={d}
                          style={{
                            textAlign: 'left',
                            padding: 6,
                            borderBottom: '1px solid #e0d9cf'
                          }}
                        >
                          {d}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {prows.map((r) => (
                      <tr key={r.uuid || r.row_index}>
                        <td
                          style={{
                            padding: 6,
                            borderBottom: '1px solid #f0ece5',
                            whiteSpace: 'nowrap',
                            color: '#7a6f65'
                          }}
                        >
                          {r.time_label}
                        </td>
                        {DAY_KEYS.map((dk) => {
                          const cell = (r.cells && r.cells[dk]) || {}
                          const fixed = cell.kind === 'FIXED'
                          return (
                            <td
                              key={dk}
                              style={{
                                padding: 6,
                                borderBottom: '1px solid #f0ece5',
                                color: fixed ? '#9a8f82' : '#3a322b'
                              }}
                            >
                              {cell.label || ''}
                            </td>
                          )
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Box>
          )
        })()}
        <Box mt={2} display="flex" style={{ gap: 12 }}>
          {forcePicker && !data.needs_claim && (
            <Button size="small" onClick={() => setForcePicker(false)}>
              ◀ Back to the grid
            </Button>
          )}
          {onClose && (
            <Button size="small" onClick={onClose}>
              Close
            </Button>
          )}
        </Box>
      </Box>
    )
  }

  const rows = data.timetable_rows || []
  const rooms = data.rooms || []
  const layouts = [...(data.layouts || []), ...extraLayouts]
  const inventory = data.inventory || []
  const d = draft || {}

  return (
    <Box mt={1}>
      <Box display="flex" alignItems="center" style={{ gap: 12 }} mb={1}>
        <Chip
          size="small"
          label={submitted ? 'Submitted' : 'Draft'}
          style={{
            backgroundColor: submitted ? '#2d9d63' : '#efe9df',
            color: submitted ? '#fff' : '#7a6f65',
            fontWeight: 600
          }}
        />
        <Typography variant="caption" color="textSecondary">
          {cells.length} of {data.quota_total || 0} sessions planned
        </Typography>
        {!submitted && (
          <Button size="small" onClick={() => setForcePicker(true)}>
            Change timetable
          </Button>
        )}
        <Box style={{ flexGrow: 1 }} />
        {submitted ? (
          <Button size="small" onClick={() => changeStatus('reopen')}>
            Reopen to edit
          </Button>
        ) : (
          <Button
            size="small"
            variant="contained"
            color="primary"
            onClick={() => changeStatus('submit')}
            disabled={!cells.length}
          >
            Submit plan
          </Button>
        )}
        {onClose && (
          <Button size="small" onClick={onClose}>
            Close
          </Button>
        )}
      </Box>

      {/* Weekly grid */}
      <Box style={{ overflowX: 'auto' }}>
        <table
          style={{ borderCollapse: 'collapse', width: '100%', minWidth: 720 }}
        >
          <thead>
            <tr>
              <th style={{ width: 90, padding: 6 }} />
              {DAY_LABELS.map((day) => (
                <th
                  key={day}
                  style={{ padding: 6, fontSize: 13, color: '#7a6f65' }}
                >
                  {day}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.uuid}>
                <td
                  style={{
                    padding: 6,
                    fontSize: 11,
                    color: '#9a8f82',
                    whiteSpace: 'nowrap',
                    verticalAlign: 'top'
                  }}
                >
                  {row.time_label}
                </td>
                {DAY_KEYS.map((dk, weekday) => {
                  const meta = (row.cells || {})[dk] || {}
                  const fixed = meta.kind === 'FIXED'
                  const cell = cellFor(row.row_index, weekday)
                  return (
                    <td key={dk} style={{ padding: 4, verticalAlign: 'top' }}>
                      <Box
                        onClick={() => openSlot(row, weekday)}
                        style={{
                          minHeight: 54,
                          borderRadius: 8,
                          padding: 8,
                          fontSize: 12,
                          cursor: fixed || submitted ? 'default' : 'pointer',
                          border: cell
                            ? '1px solid #2d6a4f'
                            : '1px dashed #d8d2c8',
                          background: fixed
                            ? '#f2efe9'
                            : cell
                            ? '#f3f9f5'
                            : '#fff',
                          opacity: fixed ? 0.7 : 1
                        }}
                      >
                        {fixed ? (
                          <Typography
                            variant="caption"
                            style={{ color: '#9a8f82' }}
                          >
                            🔒 {meta.label}
                          </Typography>
                        ) : cell ? (
                          <>
                            <Typography
                              variant="caption"
                              style={{ fontWeight: 600, display: 'block' }}
                            >
                              {cell.title || '(activity)'}
                            </Typography>
                            <Typography
                              variant="caption"
                              style={{
                                color: cell.is_custom ? '#b07b1e' : '#2d6a4f'
                              }}
                            >
                              {cell.is_custom ? '✎ your own' : '📚 repo'}
                            </Typography>
                          </>
                        ) : (
                          <Typography
                            variant="caption"
                            style={{ color: '#b9b2a7' }}
                          >
                            {meta.label ? `${meta.label} · ` : ''}＋ Plan
                          </Typography>
                        )}
                      </Box>
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </Box>

      {/* Cell editor — two steps: pick → customize */}
      <Dialog
        open={!!slot}
        onClose={() => setSlot(null)}
        fullWidth
        maxWidth="sm"
        scroll="paper"
      >
        <DialogTitle>
          {submitted ? 'View' : 'Plan'} {slot ? slot.label : 'slot'}
        </DialogTitle>
        <DialogContent dividers>
          {draft && submitted && (
            <ReadOnlyLesson d={d} layouts={layouts} rooms={rooms} />
          )}
          {draft && !submitted && step === 'pick' && (
            <Box display="flex" flexDirection="column" style={{ gap: 12 }}>
              <Box display="flex" style={{ gap: 8 }}>
                <Button
                  variant={!d.is_custom ? 'contained' : 'outlined'}
                  color={!d.is_custom ? 'primary' : 'default'}
                  onClick={() => setDraft((x) => ({ ...x, is_custom: false }))}
                >
                  From repository
                </Button>
                <Button
                  variant={d.is_custom ? 'contained' : 'outlined'}
                  color={d.is_custom ? 'primary' : 'default'}
                  onClick={() =>
                    setDraft((x) => ({ ...x, is_custom: true, activity: '' }))
                  }
                >
                  Your own
                </Button>
              </Box>

              {!d.is_custom ? (
                loadingRepo ? (
                  <CircularProgress size={22} />
                ) : !repo.length ? (
                  <Typography variant="caption" color="textSecondary">
                    No activities set for this week — use “Your own”.
                  </Typography>
                ) : (
                  repo.map((a) => (
                    <RepoCard
                      key={a.uuid}
                      activity={a}
                      picked={d.activity === a.uuid}
                      onUse={() => useActivity(a)}
                    />
                  ))
                )
              ) : (
                <>
                  <TextField
                    label="Activity title"
                    value={d.title}
                    onChange={(e) => {
                      const value = e.target.value
                      setDraft((x) => ({ ...x, title: value }))
                    }}
                  />
                  <TextField
                    label="Learning outcomes (one per line)"
                    multiline
                    minRows={2}
                    value={toLines(d.outcomes)}
                    onChange={(e) => {
                      const value = e.target.value
                      setDraft((x) => ({
                        ...x,
                        outcomes: splitLinesRaw(value)
                      }))
                    }}
                    onBlur={() =>
                      setDraft((x) => ({
                        ...x,
                        outcomes: fromLines(toLines(x.outcomes))
                      }))
                    }
                  />
                  <TextField
                    label="Steps (one per line)"
                    multiline
                    minRows={2}
                    value={stepsToText(d.steps)}
                    onChange={(e) => {
                      const value = e.target.value
                      setDraft((x) => ({
                        ...x,
                        steps: textToStepsRaw(value)
                      }))
                    }}
                    onBlur={() =>
                      setDraft((x) => ({
                        ...x,
                        steps: textToSteps(stepsToText(x.steps))
                      }))
                    }
                  />
                  <TextField
                    label="Materials used (one per line)"
                    multiline
                    minRows={2}
                    value={toLines(d.materials)}
                    onChange={(e) => {
                      const value = e.target.value
                      setDraft((x) => ({
                        ...x,
                        materials: splitLinesRaw(value)
                      }))
                    }}
                    onBlur={() =>
                      setDraft((x) => ({
                        ...x,
                        materials: fromLines(toLines(x.materials))
                      }))
                    }
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setStep('customize')}
                    disabled={!d.title}
                  >
                    Continue →
                  </Button>
                </>
              )}
            </Box>
          )}

          {draft && !submitted && step === 'customize' && (
            <Box display="flex" flexDirection="column" style={{ gap: 12 }}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                  {d.title || '(activity)'}
                </Typography>
                <Button size="small" onClick={() => setStep('pick')}>
                  ← Change activity
                </Button>
              </Box>
              {/* Layout — selectable chips + add-your-own */}
              <Box>
                <Typography variant="subtitle2">📐 Layout</Typography>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  display="block"
                  gutterBottom
                >
                  How are the children arranged?
                </Typography>
                <Box display="flex" flexWrap="wrap" style={{ gap: 8 }}>
                  {layouts.map((l) => (
                    <Chip
                      key={l.uuid}
                      label={`${l.icon || ''} ${l.label}`}
                      clickable
                      color={d.layout === l.uuid ? 'primary' : 'default'}
                      variant={d.layout === l.uuid ? 'default' : 'outlined'}
                      onClick={() =>
                        setDraft((x) => ({
                          ...x,
                          layout: x.layout === l.uuid ? '' : l.uuid
                        }))
                      }
                    />
                  ))}
                </Box>
                <Box
                  display="flex"
                  alignItems="center"
                  style={{ gap: 8 }}
                  mt={1}
                >
                  <TextField
                    size="small"
                    placeholder="Add your own layout…"
                    value={newLayout}
                    onChange={(e) => setNewLayout(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        addLayout()
                      }
                    }}
                  />
                  <Button size="small" variant="outlined" onClick={addLayout}>
                    Add layout
                  </Button>
                </Box>
              </Box>

              {/* Room */}
              <FormControl
                variant="outlined"
                size="small"
                style={{ minWidth: 200 }}
              >
                <InputLabel>Room</InputLabel>
                <Select
                  label="Room"
                  value={d.room}
                  onChange={(e) => {
                    const room = e.target.value
                    setDraft((x) => ({ ...x, room }))
                  }}
                >
                  <MenuItem value="">
                    <em>{rooms.length ? 'None' : 'No rooms configured yet'}</em>
                  </MenuItem>
                  {rooms.map((r) => (
                    <MenuItem key={r.uuid} value={r.uuid}>
                      {r.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Materials — editable rows */}
              <Box>
                <Typography variant="subtitle2">🧰 Materials</Typography>
                {(d.materials || []).map((m, i) => (
                  <Box
                    key={i}
                    display="flex"
                    alignItems="center"
                    style={{ gap: 8 }}
                    mb={1}
                  >
                    <TextField
                      fullWidth
                      size="small"
                      value={m}
                      onChange={(e) => {
                        const val = e.target.value
                        setDraft((x) => {
                          const materials = [...(x.materials || [])]
                          materials[i] = val
                          return { ...x, materials }
                        })
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={() =>
                        setDraft((x) => ({
                          ...x,
                          materials: (x.materials || []).filter(
                            (_, j) => j !== i
                          )
                        }))
                      }
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
                <Button
                  size="small"
                  onClick={() =>
                    setDraft((x) => ({
                      ...x,
                      materials: [...(x.materials || []), '']
                    }))
                  }
                >
                  ＋ Add material
                </Button>
              </Box>

              {/* Steps — editable list (title + text) */}
              <Box>
                <Typography variant="subtitle2">📝 Steps</Typography>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  display="block"
                  gutterBottom
                >
                  Edit the activity’s steps for your group — changes only affect
                  this slot, not the repository.
                </Typography>
                {(d.steps || []).map((s, i) => (
                  <Box
                    key={i}
                    display="flex"
                    alignItems="flex-start"
                    style={{ gap: 8 }}
                    mb={1}
                  >
                    <Box flexGrow={1}>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Step title"
                        value={s.label || ''}
                        onChange={(e) => {
                          const val = e.target.value
                          setDraft((x) => {
                            const steps = [...(x.steps || [])]
                            steps[i] = { ...steps[i], label: val }
                            return { ...x, steps }
                          })
                        }}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        multiline
                        minRows={2}
                        placeholder="What happens in this step?"
                        value={s.text || ''}
                        onChange={(e) => {
                          const val = e.target.value
                          setDraft((x) => {
                            const steps = [...(x.steps || [])]
                            steps[i] = { ...steps[i], text: val }
                            return { ...x, steps }
                          })
                        }}
                        style={{ marginTop: 4 }}
                      />
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() =>
                        setDraft((x) => ({
                          ...x,
                          steps: (x.steps || []).filter((_, j) => j !== i)
                        }))
                      }
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
                <Button
                  size="small"
                  onClick={() =>
                    setDraft((x) => ({
                      ...x,
                      steps: [
                        ...(x.steps || []),
                        {
                          label: `Step ${(x.steps || []).length + 1}`,
                          text: ''
                        }
                      ]
                    }))
                  }
                >
                  ＋ Add step
                </Button>
              </Box>

              {/* Vocabulary — tag chips */}
              <Box>
                <Typography variant="subtitle2">💬 Vocabulary</Typography>
                <Box
                  display="flex"
                  flexWrap="wrap"
                  style={{ gap: 6 }}
                  mb={1}
                  mt={0.5}
                >
                  {(d.vocabulary || []).map((w, i) => (
                    <Chip
                      key={i}
                      label={w}
                      onDelete={() =>
                        setDraft((x) => ({
                          ...x,
                          vocabulary: (x.vocabulary || []).filter(
                            (_, j) => j !== i
                          )
                        }))
                      }
                    />
                  ))}
                </Box>
                <TextField
                  size="small"
                  placeholder="Add a word and press Enter"
                  value={vocabInput}
                  onChange={(e) => setVocabInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      const w = vocabInput.trim()
                      if (w) {
                        setDraft((x) => ({
                          ...x,
                          vocabulary: [...(x.vocabulary || []), w]
                        }))
                        setVocabInput('')
                      }
                    }
                  }}
                />
              </Box>

              {/* Differentiation — Simpler / Extension / Specific Support */}
              <Box>
                <Typography variant="subtitle2">🎯 Differentiation</Typography>
                {[
                  {
                    key: 'simpler',
                    label: '🌱 Simpler',
                    hint: 'For children needing more support',
                    bg: '#f1f8f2',
                    border: '#bfe0c4'
                  },
                  {
                    key: 'extension',
                    label: '🚀 Extension',
                    hint: 'For children ready for more challenge',
                    bg: '#eef3fb',
                    border: '#c2d4ee'
                  },
                  {
                    key: 'specific',
                    label: '🤝 Specific Support',
                    hint: 'Individual children or accommodations',
                    bg: '#fdf7e8',
                    border: '#efdca6'
                  }
                ].map((box) => (
                  <Paper
                    key={box.key}
                    variant="outlined"
                    style={{
                      padding: 10,
                      marginTop: 8,
                      background: box.bg,
                      borderColor: box.border
                    }}
                  >
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="baseline"
                    >
                      <Typography variant="body2" style={{ fontWeight: 600 }}>
                        {box.label}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {box.hint}
                      </Typography>
                    </Box>
                    <TextField
                      fullWidth
                      size="small"
                      multiline
                      minRows={2}
                      value={(d.differentiation || {})[box.key] || ''}
                      onChange={(e) => {
                        const val = e.target.value
                        setDraft((x) => ({
                          ...x,
                          differentiation: {
                            ...x.differentiation,
                            [box.key]: val
                          }
                        }))
                      }}
                      style={{ marginTop: 6, background: '#fff' }}
                    />
                  </Paper>
                ))}
              </Box>

              {inventory.length > 0 && (
                <>
                  <Divider />
                  <Typography variant="caption" color="textSecondary">
                    Reserve tracked materials
                  </Typography>
                  {inventory.map((it) => {
                    const res = (d.reservations || []).find(
                      (r) => r.inventory_item_id === it.uuid
                    )
                    return (
                      <Box
                        key={it.uuid}
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Typography variant="body2">
                          {it.name}{' '}
                          <span style={{ color: '#9a8f82' }}>
                            ({it.quantity})
                          </span>
                        </Typography>
                        <TextField
                          type="number"
                          size="small"
                          style={{ width: 80 }}
                          value={res ? res.qty : ''}
                          onChange={(e) => {
                            const qty = Number(e.target.value) || 0
                            setDraft((x) => {
                              const others = (x.reservations || []).filter(
                                (r) => r.inventory_item_id !== it.uuid
                              )
                              return {
                                ...x,
                                reservations: qty
                                  ? [
                                      ...others,
                                      { inventory_item_id: it.uuid, qty }
                                    ]
                                  : others
                              }
                            })
                          }}
                        />
                      </Box>
                    )
                  })}
                </>
              )}

              {warnings && (
                <Paper
                  variant="outlined"
                  style={{
                    padding: 8,
                    background: '#fff8e1',
                    borderColor: '#f0c36d'
                  }}
                >
                  {(warnings.room || []).map((w, i) => (
                    <Typography key={`r${i}`} variant="caption" display="block">
                      ⚠ Room: {w}
                    </Typography>
                  ))}
                  {(warnings.inventory || []).map((w, i) => (
                    <Typography key={`i${i}`} variant="caption" display="block">
                      ⚠ Inventory: {w}
                    </Typography>
                  ))}
                </Paper>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {!submitted && slot && cellFor(slot.rowIndex, slot.weekday) && (
            <Button
              onClick={clearCell}
              style={{ color: '#9b2226', marginRight: 'auto' }}
              disabled={saving}
            >
              Clear slot
            </Button>
          )}
          <Button onClick={() => setSlot(null)}>Close</Button>
          {!submitted && step === 'customize' && (
            <Button
              color="primary"
              variant="contained"
              onClick={save}
              disabled={saving || !d.title}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default PlanEditor
